import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";

import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import BasicInfoForm from "./basic-info-form";
import PriceDiscountTaxForm from "./prices-renewal-form";
import FacilityCreditsForm from "./facility-credits-form";

import {
  useCreateMembershipsMutation,
  useUpdateMembershipsMutation,
} from "@/services/membershipsApi";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { ErrorType, FormProps, LimitedAccessTime } from "@/types";
import {
  cleanLimitedAccessTime,
  replaceNullWithDefaults,
  validateSchedule,
} from "@/lib/helper";
import { Membership } from "@/types/system-setting/membership";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { MembershipStatus, MembershipType } from "@/types/enums";
import { cn } from "@/lib/utils";


const stepContentComponents = [
  BasicInfoForm, // Step 1
  PriceDiscountTaxForm, // Step 2
  FacilityCreditsForm, // Step 3
];

const getStepContent = (step: number) => {
  const Component = stepContentComponents[step - 1];
  return Component ? <Component /> : null;
};

const steps = [
  { label: "Basic Information and Scope", key: 1 },
  { label: "Pricing & Contract Renewal", key: 2 },
  { label: "Facility details", key: 3 },
];


const MembershipForm = ({
  isOpen,
  setOpen,
  data,
  action,
  setAction,
  setData,
  refetch,
}: FormProps<Membership>) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const userId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.id) || 0;

  const { toast } = useToast();
  const [activeStep, setActiveStep] = useState(1);

  const defaultValue = {
    org_id: orgId,
    name: "",
    group_id: undefined,
    description: "",
    status: MembershipStatus.active,
    access_type: "no_restriction",
    limited_access_time: {},
    duration_period: "",
    duration: undefined,
    price_master_id: undefined,
    income_category_id: undefined,
    tax_group_id: undefined,
    billing_cycle: "",
    auto_renewal: false,
    prolongation_period: undefined,
    days_before: 1,
    next_invoice: 1,
    renewal_details: {},
    facilities: [],
    type: MembershipType.Default,
  };
  

  const form = useForm<Membership>({
    mode: "all",
    defaultValues: defaultValue,
  });

  const [createMemberships] = useCreateMembershipsMutation();
  const [updateMemberships] = useUpdateMembershipsMutation();

  const {
    trigger,
    handleSubmit,
    setError,
    getValues,
    clearErrors,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  const access_type = getValues("access_type");

  useEffect(() => {
    if (!isOpen) return; // Only reset when form is open
    
    if (action == "edit" && data != undefined) {
      const updatedObject = {
        ...data,
        ...data?.renewal_details,
        limited_access_time: replaceNullWithDefaults(data?.limited_access_time),
        days_before: 1,
        next_invoice: 1,
      };

      console.log({ updatedObject }, "edit");
      reset(updatedObject as Membership, { 
        keepIsSubmitted: false, 
        keepSubmitCount: false,
        keepDirtyValues: false,
        keepDefaultValues: false
      });
    } else {
      // Create a fresh defaultValue object to ensure no stale references
      const freshDefaultValue = {
        org_id: orgId,
        name: "",
        group_id: undefined,
        description: "",
        status: MembershipStatus.active,
        access_type: "no_restriction",
        limited_access_time: {},
        duration_period: "",
        duration: undefined,
        price_master_id: undefined,
        income_category_id: undefined,
        tax_group_id: undefined,
        billing_cycle: "",
        auto_renewal: false,
        prolongation_period: undefined,
        days_before: 1,
        next_invoice: 1,
        renewal_details: {},
        facilities: [],
        type: MembershipType.Default,
      };
      console.log({ freshDefaultValue }, "add");
      reset(freshDefaultValue, { 
        keepIsSubmitted: false, 
        keepSubmitCount: false,
        keepDirtyValues: false,
        keepDefaultValues: false
      });
    }
  }, [data, action, reset, isOpen, orgId]);

  const onSubmit = async (formData: Membership) => {
    const payload = formData;

    // make renewal data,
    convertStringNumbersToObject(payload);
    payload.org_id = orgId;
    payload.renewal_details = {};

    if (payload.access_type == "no_restriction") {
      payload.limited_access_time = {};
    } else {
      payload.limited_access_time = cleanLimitedAccessTime(
        payload?.limited_access_time as LimitedAccessTime
      );
    }

    if (payload.auto_renewal && payload.prolongation_period) {
      payload.renewal_details = {
        prolongation_period: payload.prolongation_period,
      };
    } else {
      payload.renewal_details = {};
    }

    if (payload.facilities.length > 0) {
      const validate: { check: boolean; reason: string } = {
        check: false,
        reason: "",
      };

      // Validate each facility item
      for (const item of payload.facilities) {
        // Check credit type
        if (!item.credit_type) {
          validate.check = true;
          validate.reason = "Credit type is required for all facilities";
          break;
        }

        if (item.credit_type === "amount") {
          // Validate validity fields
          if (item.validity.duration == null || item.validity.duration == undefined) {
            validate.check = true;
            validate.reason = "Validity duration is required";
            break;
          }

          if (!item.validity.type) {
            validate.check = true;
            validate.reason = "Validity type is required";
            break;
          }

          // Check validity doesn't exceed membership duration
          if (
            item.validity.type &&
            item.validity.duration !== null &&
            getDaysCheck(
              payload.duration as number,
              payload.duration_period as string,
              item.validity.duration as number,
              item.validity.type as string
            )
          ) {
            validate.check = true;
            validate.reason = "Validity cannot be greater than membership duration";
            break;
          }

          // Validate frequency fields
          if (item.frequency.duration == null || item.frequency.duration == undefined) {
            validate.check = true;
            validate.reason = "Frequency duration is required";
            break;
          }

          if (!item.frequency.type) {
            validate.check = true;
            validate.reason = "Frequency type is required";
            break;
          }

          // Check frequency doesn't exceed membership duration
          if (
            item.frequency.type &&
            item.frequency.duration !== null &&
            getDaysCheck(
              payload.duration as number,
              payload.duration_period as string,
              item.frequency.duration as number,
              item.frequency.type as string
            )
          ) {
            validate.check = true;
            validate.reason = "Frequency cannot be greater than membership duration";
            break;
          }
        }
      }

      if (validate.check) {
        toast({
          variant: "destructive",
          title: validate.reason,
        });
        return;
      }
    } else {
      toast({
        variant: "destructive",
        title: "Select at least one facility credit detail",
      });
      return;
    }

    if (action == "add") {
      delete payload?.id;
    }

    try {
      if (action == "add") {
        const resp = await createMemberships(payload).unwrap();
        if (resp) {
          console.log({ resp });
          refetch();
          toast({
            variant: "success",
            title: "Membership added successfully.",
          });
          handleClose();
        }
      } else {
        const resp = await updateMemberships(payload).unwrap();
        if (resp) {
          console.log({ resp });
          toast({
            variant: "success",
            title: "Membership updated successfully.",
          });
          handleClose();
        }
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
      // handleClose();
      // setData(undefined);
      // setActiveStep(1);
    }
  };

  const handleNext = async () => {
    const isStepValid = await trigger(undefined, { shouldFocus: true });
    const limitedAccessData = getValues(
      "limited_access_time"
    ) as LimitedAccessTime;

    // const timeSlots = validateSchedule(limitedAccessData)

    // if(!timeSlots.isValid){
    //   toast({
    //     variant: "destructive",
    //     title: timeSlots.message,
    //   });
    //   return;
    // }

    if (activeStep === 1 && access_type === "limited-access") {
      console.log("inside step 1");

      // Check for time conflicts within each day
      const hasTimeConflicts = Object.keys(limitedAccessData).some((day) => {
        const slots = limitedAccessData[day];
        return slots.some((currentSlot, index) => {
          // Check each slot against the subsequent slots in the same day
          return slots
            .slice(index + 1)
            .some((otherSlot) =>
              isTimeOverlap(
                currentSlot.from_time,
                currentSlot.to_time,
                otherSlot.from_time,
                otherSlot.to_time
              )
            );
        });
      });

      if (hasTimeConflicts) {
        toast({
          variant: "destructive",
          title: "Time slots overlap on the same day",
        });
        return;
      }

      // Check for missing or incomplete time entries
      const hasCompleteSlot = Object.values(limitedAccessData).some((slots) =>
        slots.some((slot) => slot.from_time !== "" && slot.to_time !== "")
      );

      const hasMissingFrom = Object.values(limitedAccessData).some((slots) =>
        slots.some((slot) => slot.from_time === "" && slot.to_time !== "")
      );

      const hasMissingTo = Object.values(limitedAccessData).some((slots) =>
        slots.some((slot) => slot.from_time !== "" && slot.to_time === "")
      );

      if (hasMissingFrom) {
        toast({
          variant: "destructive",
          title: "Missing 'from' time in a time slot",
        });
        return;
      }

      if (hasMissingTo) {
        toast({
          variant: "destructive",
          title: "Missing 'to' time in a time slot",
        });
        return;
      }

      if (!hasCompleteSlot) {
        toast({
          variant: "destructive",
          title: "At least one day needs a complete time slot",
        });
        return;
      }
    }

    // Proceed to the next step if validation passes
    if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    // Create a fresh defaultValue object to ensure complete reset
    const freshDefaultValue = {
      org_id: orgId,
      name: "",
      group_id: undefined,
      description: "",
      status: MembershipStatus.active,
      access_type: "no_restriction",
      limited_access_time: {},
      duration_period: "",
      duration: undefined,
      price_master_id: undefined,
      income_category_id: undefined,
      tax_group_id: undefined,
      billing_cycle: "",
      auto_renewal: false,
      prolongation_period: undefined,
      days_before: 1,
      next_invoice: 1,
      renewal_details: {},
      facilities: [],
    };
    
    reset(freshDefaultValue, { 
      keepIsSubmitted: false, 
      keepSubmitCount: false,
      keepDirtyValues: false,
      keepDefaultValues: false
    });
    clearErrors();
    setAction("add");
    setActiveStep(1);
    setData(undefined);
    setOpen(false);
  };

  console.log({ watcher, errors });


  const sheetWidthClass =
    activeStep === 3
      ? "lg:!max-w-[1100px] xl:!max-w-[1240px]"
      : "lg:!max-w-[1000px]";

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        className={cn(`bg-formbg w-full p-0 custom-scrollbar h-full `, sheetWidthClass)}
        hideCloseButton
        onOpenAutoFocus={(e: Event) => e.preventDefault()}
      >
        <SheetHeader className="px-3 sm:px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
          <div className="flex justify-between gap-2 sm:gap-5 items-center">
            <div className="flex items-start gap-3 min-w-0">
              <button type="button" onClick={handleClose}>
                <i className="fa fa-xmark text-xl"></i>
              </button>
              <div className="min-w-0">
                <h1 className="font-medium truncate">
                  {action == "add" ? "Create" : "Edit"} Membership
                </h1>
                <CompactStepIndicator
                  activeStep={activeStep}
                  steps={steps}
                />
              </div>
            </div>
            <div className="flex justify-center gap-1.5 sm:gap-[20px] shrink-0">
              <Button
                type="button"
                className="hidden sm:flex w-[100px] text-center items-center gap-2 border-primary"
                variant={"outline"}
                onClick={handleClose}
              >
                <i className="fa fa-xmark "></i>
                Cancel
              </Button>

              {activeStep > 1 && (
                <Button
                  className="w-9 sm:w-[100px] px-0 sm:px-2 text-center flex items-center justify-center gap-2 border-primary"
                  type="button"
                  variant={"outline"}
                  onClick={handleBack}
                >
                  <i className="fa fa-arrow-left-long "></i>
                  <span className="hidden sm:inline">Previous</span>
                </Button>
              )}

              {activeStep === 3 ? (
                <LoadingButton
                  type="submit"
                  className="w-9 sm:w-[100px] px-0 sm:px-2 bg-primary text-black text-center flex items-center justify-center gap-2"
                  onClick={handleSubmit(onSubmit)}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {!isSubmitting && (
                    <i className="fa-regular fa-floppy-disk text-base sm:px-1 "></i>
                  )}
                  <span className="hidden sm:inline">Save</span>
                </LoadingButton>
              ) : (
                <Button
                  type="button"
                  className="w-9 sm:w-[100px] px-0 sm:px-2 bg-primary text-black text-center flex items-center justify-center gap-2"
                  onClick={handleNext}
                >
                  <i className="fa fa-arrow-right-long "></i>
                  <span className="hidden sm:inline">Next</span>
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <FormProvider {...form} key={`${action}-${data?.id || 'new'}`}>
          <form noValidate className="pt-4 px-3 sm:px-6">
            {getStepContent(activeStep)}
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default MembershipForm;

function convertStringNumbersToObject(obj: any): void {
  Object.keys(obj).forEach((key) => {
    if (
      typeof obj[key!] === "string" &&
      !isNaN(obj[key!]) &&
      obj[key!].trim() !== ""
    ) {
      obj[key!] = Number(obj[key!]);
    } else if (typeof obj[key!] === "object" && obj[key!] !== null) {
      convertStringNumbersToObject(obj[key!]);
    }
  });
}

// for validation

function getDays(durationNo: number, durationType: string): number {
  console.log({ durationType, durationNo });
  switch (durationType) {
    case "week":
      return durationNo * 7; // 7 days per week
    case "month":
      return durationNo * 30; // Approximate average days in a month
    case "quarter":
      return durationNo * 120; // Approximate average days in 4 months
    case "year":
      return durationNo * 365; // Approximate days in a year
    case "contract_duration":
      return durationNo * 0;
    default:
      return 0;
  }
}

function getDaysCheck(
  durationNo: number,
  durationType: string,
  itemdurationNo: number,
  itemdurationType: string
): boolean {
  const memberShipDays: number = getDays(durationNo, durationType);
  const creditsDays: number = getDays(itemdurationNo, itemdurationType);

  console.log(memberShipDays < creditsDays, { creditsDays, memberShipDays });
  return memberShipDays < creditsDays;
}

function isTimeOverlap(
  from1: string,
  to1: string,
  from2: string,
  to2: string
): boolean {
  const [f1, t1] = [
    new Date(`1970-01-01T${from1}:00Z`),
    new Date(`1970-01-01T${to1}:00Z`),
  ];
  const [f2, t2] = [
    new Date(`1970-01-01T${from2}:00Z`),
    new Date(`1970-01-01T${to2}:00Z`),
  ];
  return f1 < t2 && f2 < t1;
}
